import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useTranslation from 'hooks/use-translation';
import RangeSlider from 'components/core/range-slider';
import RangeSliderPlum from 'components/core/range-slider/range-slider-plum';

type DistanceFilterProps = {
  value: number;
  onChange: (distance: number) => void;
  onChangeCommitted?: (distance: number) => void;
};

export function DistanceFilter({ value, onChange, onChangeCommitted }: DistanceFilterProps): JSX.Element {
  const { Trans }: any = useTranslation();
  const flags = useFlags();
  const Slider = flags['growth.ecomm.homepage-v3.rollout'] ? StyledRangeSliderPlum : StyledRangeSlider;

  const pluralizedMileString = value === 1 ? `mile` : `miles`;

  return (
    <>
      <Slider
        aria-labelledby='distance-text'
        getAriaLabel={() => `Filter dispensaries by distance`}
        getAriaValueText={() => `Filter dispensaries by distance`}
        max={50}
        min={1}
        onChange={(_e, newValue: number) => onChange(newValue)}
        onChangeCommitted={(_e, newValue: number) => onChangeCommitted?.(newValue)}
        value={value}
      />
      <SliderLabel>
        <Trans i18nKey='dispensaries.distance-filter'>
          Show dispensaries within{' '}
          <SliderLabelType>
            {value} {pluralizedMileString}
          </SliderLabelType>
          of your location.
        </Trans>
      </SliderLabel>
    </>
  );
}

const sliderStyles = `
  margin: 14px 0 0;
`;

const responsiveStyles = ({ theme }: { theme: any }): string => `
  ${theme.breakpoints.down(`sm`)} {
    &&& {
      width: 80%;
      .MuiSlider-rail { height: 12px; }
      .MuiSlider-track { height: 12px; }
      .MuiSlider-thumb { height: 25px; width: 25px; margin-top: -7px; }
    }
  }
`;

const StyledRangeSlider = styled(RangeSlider)`
  ${sliderStyles}
  ${responsiveStyles}
`;

const StyledRangeSliderPlum = styled(RangeSliderPlum)`
  ${sliderStyles}
  ${responsiveStyles}
`;

const SliderLabel = styled.div`
  font-size: 12px;
  line-height: 19px;
  padding: 0 15px 0 0;
  margin-top: 6px;
`;

const SliderLabelType = styled.b`
  margin-right: 4px;
`;
